import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { all_routes } from '../../router/all_routes'
import {
  downloadCertificate,
  getStoredCertificateName,
  prepareCertificateDownload,
  setStoredCertificateName,
  CertificateMeta,
} from '../../../utils/helpers'
import {
  getCBTDetail,
  saveCBTAnswer,
  submitCBTAnswers,
  CBTData,
  CBTQuestion,
  CBTSubmitRequest,
  CBTSubmitResponse,
  CBTUserAnswer,
  flagFinishAttempt,
} from '../../../api/courses'

const CBTTest = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const finishingRef = useRef(false)
  const autosaveQueueRef = useRef<Promise<void>>(Promise.resolve())

  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [answers, setAnswers] = useState<number[]>([])
  const [timeLeft, setTimeLeft] = useState(60 * 60) // 60 minutes in seconds
  const [isTestComplete, setIsTestComplete] = useState(false)
  const [testResult, setTestResult] = useState<{
    score: number
    correctAnswers: number
    totalQuestions: number
    isPassed: boolean
    timeUsed: number
    userAnswers?: CBTUserAnswer[]
  } | null>(null)
  const [showCertificateModal, setShowCertificateModal] = useState(false)
  const [certificateName, setCertificateName] = useState('')
  const [storedCertificateName, setStoredCertificateNameState] = useState('')

  // API data states
  const [cbtData, setCbtData] = useState<CBTData | null>(null)
  const [questions, setQuestions] = useState<CBTQuestion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [doubtful, setDoubtful] = useState<boolean[]>([])
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)
  const [showFinishingCourse, setShowFinishingCourse] = useState(false)
  const [attemptId, setAttemptId] = useState<number | null>(null)

  // Fetch CBT data from API
  useEffect(() => {
    const fetchCBTData = async () => {
      if (!id) {
        setError('Course ID not found')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const storedAttemptId = localStorage.getItem(`cbtAttemptId:${id}`)
        const parsedAttemptId = storedAttemptId ? Number(storedAttemptId) : NaN
        if (!Number.isInteger(parsedAttemptId) || parsedAttemptId <= 0) {
          throw new Error('Sesi ujian tidak ditemukan. Silakan mulai ujian kembali.')
        }
        setAttemptId(parsedAttemptId)

        const response = await getCBTDetail(id)
        const cbtDataObj = response.data

        // Parse questions safely
        let rawQuestions: any[] = []
        if (Array.isArray(cbtDataObj?.questions)) {
          rawQuestions = cbtDataObj.questions
        } else if (typeof cbtDataObj?.questions === 'string') {
          try {
            rawQuestions = JSON.parse(cbtDataObj.questions)
          } catch {
            rawQuestions = []
          }
        }

        // Parse duration safely (fallback to 60 minutes if 0/null/empty)
        const parsedDuration = parseInt(cbtDataObj?.duration)
        const durationInSeconds =
          !isNaN(parsedDuration) && parsedDuration > 0
            ? Math.floor(parsedDuration / 1000)
            : 3600 // 60 minutes fallback

        setCbtData(cbtDataObj)
        setQuestions(rawQuestions)

        const savedAnswers = localStorage.getItem(`cbtAnswers:${id}`)
        if (savedAnswers) {
          try {
            const parsedAnswers = JSON.parse(savedAnswers)
            if (
              Array.isArray(parsedAnswers) &&
              parsedAnswers.length === rawQuestions.length
            ) {
              setAnswers(parsedAnswers)
            } else {
              setAnswers(new Array(rawQuestions.length).fill(-1))
            }
          } catch {
            setAnswers(new Array(rawQuestions.length).fill(-1))
          }
        } else {
          setAnswers(new Array(rawQuestions.length).fill(-1))
        }

        const savedQuestion = Number(
          localStorage.getItem(`cbtCurrentQuestion:${id}`),
        )
        setCurrentQuestion(
          Number.isInteger(savedQuestion) &&
            savedQuestion >= 0 &&
            savedQuestion < rawQuestions.length
            ? savedQuestion
            : 0,
        )

        // Set timer based on persisted start time (so refresh doesn't reset)
        try {
          const startKey = `cbtStartTime:${id}`
          const storedStart = startKey ? localStorage.getItem(startKey) : null
          let startTimestamp = storedStart ? parseInt(storedStart) : NaN
          if (Number.isNaN(startTimestamp)) {
            startTimestamp = Date.now()
            if (startKey) localStorage.setItem(startKey, String(startTimestamp))
          }
          const elapsedSeconds = Math.floor(
            (Date.now() - startTimestamp) / 1000,
          )
          const remaining = Math.max(0, durationInSeconds - elapsedSeconds)
          // if remaining is 0, set to 1 so timer effect triggers auto-submit next tick
          setTimeLeft(remaining === 0 ? 1 : remaining)
        } catch {
          setTimeLeft(durationInSeconds)
        }

        // Initialize doubtful array
        setDoubtful(new Array(rawQuestions.length).fill(false))
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            'Failed to load CBT data',
        )
      } finally {
        setLoading(false)
      }
    }

    fetchCBTData()
  }, [id])

  useEffect(() => {
    setStoredCertificateNameState(getStoredCertificateName(cbtData?.id || id))
  }, [cbtData?.id, id])

  const savedCertificateName =
    cbtData?.certificate_downloads?.name || storedCertificateName

  const handleFinishTest = useCallback(async () => {
    if (!questions.length || !cbtData || !attemptId || finishingRef.current) return

    finishingRef.current = true
    setSubmitting(true)
    try {
      await autosaveQueueRef.current

      // Prepare answers data for API
      const answersData: CBTSubmitRequest = {
        course_id: cbtData.id,
        attempt_id: attemptId,
        answers: answers
          .map((answerIndex, questionIndex) => {
            if (answerIndex === undefined || answerIndex === -1) {
              return null
            }
            const question = questions[questionIndex]
            const selectedAnswer = question.answers[answerIndex]
            if (!selectedAnswer) return null
            return {
              question_id: question.id,
              answer_id: selectedAnswer.id,
            }
          })
          .filter(Boolean) as { question_id: number; answer_id: number }[],
      }

      // Submit answers to API
      const response: CBTSubmitResponse = await submitCBTAnswers(answersData)

      setIsTestComplete(true)

      // Clear persisted start time on completion
      try {
        if (id) {
          localStorage.removeItem(`cbtStartTime:${id}`)
          localStorage.removeItem(`cbtAttemptId:${id}`)
          localStorage.removeItem(`cbtAnswers:${id}`)
          localStorage.removeItem(`cbtCurrentQuestion:${id}`)
        }
      } catch { }

      // Calculate is_passed based on score (assuming Number(cbtData.min_passing_score)% is passing)
      const scoreNumber = parseFloat(response.data.score.replace('%', ''))
      const isPassed = scoreNumber >= Number(cbtData.min_passing_score)

      setTestResult({
        score: scoreNumber,
        correctAnswers: response.data.correct_count,
        totalQuestions: response.data.total_questions,
        isPassed,
        timeUsed:
          (cbtData ? Math.floor(parseInt(cbtData.duration) / 1000) : 60 * 60) -
          timeLeft,
        userAnswers: response.data.user_answers,
      })
    } catch (err) {
      console.error('Error submitting CBT answers:', err)
      setError(
        (err as any)?.response?.data?.error ||
          (err as any)?.response?.data?.message ||
          'Jawaban belum berhasil disimpan. Silakan coba submit kembali.',
      )
      finishingRef.current = false
    } finally {
      setSubmitting(false)
    }
  }, [answers, timeLeft, questions, cbtData, id, attemptId])

  // Timer effect
  useEffect(() => {
    if (timeLeft > 0 && !isTestComplete && !loading) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleFinishTest()
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [timeLeft, isTestComplete, loading, handleFinishTest])

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const handleAnswerSelect = (answerIndex: number) => {
    setAnswers((previousAnswers) => {
      const newAnswers = [...previousAnswers]
      newAnswers[currentQuestion] = answerIndex
      if (id) {
        localStorage.setItem(`cbtAnswers:${id}`, JSON.stringify(newAnswers))
      }
      return newAnswers
    })

    const question = questions[currentQuestion]
    const selectedAnswer = question?.answers?.[answerIndex]
    if (cbtData && attemptId && question && selectedAnswer) {
      autosaveQueueRef.current = autosaveQueueRef.current
        .then(() =>
          saveCBTAnswer({
            course_id: cbtData.id,
            attempt_id: attemptId,
            question_id: question.id,
            answer_id: selectedAnswer.id,
          }),
        )
        .then(() => undefined)
        .catch((err) => {
          console.error('Error autosaving CBT answer:', err)
          setError(
            err?.response?.data?.error ||
              'Jawaban belum tersimpan ke server. Silakan coba lagi.',
          )
        })
    }
  }

  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((previousQuestion) => {
        const nextQuestion = previousQuestion + 1
        if (id) {
          localStorage.setItem(
            `cbtCurrentQuestion:${id}`,
            String(nextQuestion),
          )
        }
        return nextQuestion
      })
    }
  }

  const handlePreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((previousQuestion) => {
        const nextQuestion = previousQuestion - 1
        if (id) {
          localStorage.setItem(
            `cbtCurrentQuestion:${id}`,
            String(nextQuestion),
          )
        }
        return nextQuestion
      })
    }
  }

  const handleQuestionJump = (questionIndex: number) => {
    setCurrentQuestion(questionIndex)
    if (id) {
      localStorage.setItem(
        `cbtCurrentQuestion:${id}`,
        String(questionIndex),
      )
    }
  }

  const handleGoToDashboard = () => {
    navigate(all_routes.studentCourses)
  }

  useEffect(() => {
    if (!savedCertificateName || !cbtData?.id) return
    if (
      cbtData.certificate_downloads?.certificate_number ||
      cbtData.certificate_downloads?.qr_url
    ) {
      return
    }

    const score = testResult?.score?.toString() || ''

    prepareCertificateDownload(
      Number(cbtData.id),
      savedCertificateName,
      score,
      cbtData.min_passing_score || '',
    )
  }, [
    cbtData?.id,
    cbtData?.certificate_downloads?.name,
    cbtData?.certificate_downloads?.certificate_number,
    cbtData?.certificate_downloads?.qr_url,
    cbtData?.min_passing_score,
    savedCertificateName,
    testResult?.score,
  ])

  const handleReviewTest = () => {
    navigate(all_routes.studentCBTList.replace(':id', String(attemptId || '')), {
      state: {
        userAnswers: testResult?.userAnswers || [],
      },
    })
  }

  const handleCertificateDownload = async () => {
    const certificateMeta: CertificateMeta = {
      eventDates: cbtData?.certificate_event_dates || undefined,
      jplTotal: cbtData?.certificate_jpl_total || undefined,
      jplNote: cbtData?.certificate_jpl_note || undefined,
      speakers: cbtData?.certificate_speakers || undefined,
      materials: cbtData?.certificate_materials || undefined,
      location: cbtData?.certificate_location || undefined,
      signatureDate: undefined,
      certificateNumber:
        cbtData?.certificate_downloads?.certificate_number || undefined,
      qrUrl: cbtData?.certificate_downloads?.qr_url || undefined,
    }

    // Check if certificate_downloads exists and has a name
    if (savedCertificateName) {
      // Direct download with existing name
      await downloadCertificate(
        savedCertificateName,
        undefined, // customFileName
        undefined, // pdfUrl
        savedCertificateName, // userName
        cbtData?.title, // courseTitle
        true, // useAutoGeneration
        id ? parseInt(id) : undefined, // courseId
        undefined,
        true,
        testResult.score.toString(),
        cbtData?.min_passing_score,
        certificateMeta,
      )
    } else {
      // Show modal for first time download (certificate_downloads is null)
      setShowCertificateModal(true)
    }
  }

  const confirmCertificateDownload = async () => {
    setShowCertificateModal(false)
    setStoredCertificateName(cbtData?.id || id, certificateName)
    setStoredCertificateNameState(certificateName)

    const certificateMeta: CertificateMeta = {
      eventDates: cbtData?.certificate_event_dates || undefined,
      jplTotal: cbtData?.certificate_jpl_total || undefined,
      jplNote: cbtData?.certificate_jpl_note || undefined,
      speakers: cbtData?.certificate_speakers || undefined,
      materials: cbtData?.certificate_materials || undefined,
      location: cbtData?.certificate_location || undefined,
      signatureDate: undefined,
      certificateNumber:
        cbtData?.certificate_downloads?.certificate_number || undefined,
      qrUrl: cbtData?.certificate_downloads?.qr_url || undefined,
    }
    await downloadCertificate(
      certificateName,
      undefined, // customFileName
      undefined, // pdfUrl
      certificateName, // userName
      cbtData?.title, // courseTitle
      true, // useAutoGeneration
      id ? parseInt(id) : undefined, // courseId
      undefined,
      true,
      testResult.score.toString(),
      cbtData?.min_passing_score,
      certificateMeta,
    )
  }

  const handleSubmitFlagFinish = async () => {
    const courseId = cbtData?.id.toString();
    if (!courseId || !attemptId) return
    await flagFinishAttempt({ course_id: courseId, attempt_id: attemptId });

    // handleGoToDashboard();
    navigate(all_routes.studentCBTList.replace(':id', id || ''), {
      state: {
        userAnswers: testResult?.userAnswers || [],
      },
    })
  }

  const handleRetest = () => {
    navigate(all_routes.studentCBTInstruction.replace(':id', id || ''), {
      state: {
        userAnswers: testResult?.userAnswers || [],
      },
    })
  }

  if (isTestComplete && testResult) {
    return (
      <div className="content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="card text-center">
                <div className="card-body">
                  {/* Result Circle */}
                  <div className="mb-4">
                    <div
                      className={`mx-auto rounded-circle d-flex align-items-center justify-content-center ${testResult.isPassed ? 'bg-success' : 'bg-danger'
                        }`}
                      style={{ width: '120px', height: '120px' }}
                    >
                      <h2 className="text-white mb-0">{testResult.score}</h2>
                    </div>
                  </div>

                  {/* Result Message */}
                  <h4
                    className={`mb-3 ${testResult.isPassed ? 'text-success' : 'text-danger'
                      }`}
                  >
                    {testResult.isPassed ? 'Selamat! 🎉' : 'Mohon Maaf 😞'}
                  </h4>

                  {/* Check if Still Have Chance */}
                  {cbtData?.user_attempt_count < Number(cbtData?.max_attempt) ?
                    <p className="mb-4">
                      {testResult.isPassed
                        ? `Kamu telah lulus ujian CBT. Kamu masih memiliki ${Number(cbtData?.max_attempt) - cbtData?.user_attempt_count} kesempatan untuk mengulang. Pilih Ulangi Test untuk mencoba kembali, atau Review Test untuk melihat pembahasan dan mengakhiri kesempatan ujian.`
                        : `Kamu belum lulus ujian CBT. Kamu masih memiliki ${Number(cbtData?.max_attempt) - cbtData?.user_attempt_count} kesempatan untuk mengulang. Pilih Ulangi Test untuk mencoba kembali, atau Review Test untuk melihat pembahasan dan mengakhiri kesempatan ujian.`}
                    </p>
                    :
                    <p className="mb-4">
                      {testResult.isPassed
                        ? 'Anda telah lulus ujian CBT dengan baik. Silakan lanjutkan ke tahapan berikutnya sesuai petunjuk yang tersedia.'
                        : 'Anda belum lulus ujian CBT. Jangan khawatir! Silakan pelajari kembali materi yang tersedia dan coba lagi di kesempatan berikutnya.'}
                    </p>}

                  {/* Score Details */}
                  <div className="row mb-4">
                    <div className="col-6">
                      <div className="border rounded p-3">
                        <h6 className="text-primary mb-1">
                          {testResult.correctAnswers}/
                          {testResult.totalQuestions}
                        </h6>
                        <small className="text-muted">Jawaban Benar</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="border rounded p-3">
                        <h6 className="text-warning mb-1">
                          {formatTime(testResult.timeUsed)}
                        </h6>
                        <small className="text-muted">Waktu Digunakan</small>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {cbtData?.user_attempt_count < Number(cbtData?.max_attempt) ? <div className="d-flex justify-content-center gap-3">
                    <button
                      className="btn btn-info w-100"
                      onClick={handleRetest}
                    >
                      <i className="isax isax-refresh me-2" />
                      Ulangi Test
                    </button>

                    <button
                      className="btn btn-outline-primary w-100"
                      onClick={handleReviewTest}
                    >
                      <i className="isax isax-document-text me-2" />
                      Review Test
                    </button>
                  </div> : <div className="d-flex align-items-center justify-content-center">
                    <button
                      className="btn btn-outline-primary mt-3 w-100"
                      onClick={handleReviewTest}
                    >
                      <i className="isax isax-document-text me-2" />
                      Review Test
                    </button>
                  </div>}

                  <div className="d-flex align-items-center justify-content-center">
                    <button
                      className="btn btn-success mt-3 w-100"
                      onClick={handleCertificateDownload}
                    >
                      <i className="isax isax-document-download me-2" />
                      Download Sertifikat
                    </button>
                  </div>

                  <div className="d-flex align-items-center justify-content-center">
                    <button
                      className="btn btn-primary mt-3 w-100"
                      onClick={handleGoToDashboard}
                    >
                      <i className="isax isax-home me-2" />
                      Kembali ke Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Confirmation Modal */}
        {showFinishingCourse && (
          <>
            <div className="modal-backdrop fade show"></div>
            <div
              className="modal fade show"
              style={{ display: 'block' }}
              tabIndex={-1}
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Konfirmasi Selesaikan Ujian</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowFinishingCourse(false)}
                    />
                  </div>
                  <div className="modal-body">
                    <p>
                      Apakah kamu yakin akan review ujian CBT? Kamu tidak
                      bisa mengulang kembali test setelah submit.
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowFinishingCourse(false)}
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setShowFinishingCourse(false)
                        handleSubmitFlagFinish()
                      }}
                      disabled={submitting}
                    >
                      Ya, Selesaikan Ujian
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Certificate Download Modal */}
        {showCertificateModal && (
          <>
            <div className="modal-backdrop fade show"></div>
            <div
              className="modal fade show"
              style={{ display: 'block' }}
              tabIndex={-1}
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Download Sertifikat</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowCertificateModal(false)}
                    />
                  </div>
                  <div className="modal-body">
                    <p className="mb-3">
                      Apakah Anda yakin ingin mencetak e-sertifikat dengan nama
                      yang tertera pada formulir di bawah ini?
                    </p>
                    <div className="mb-3">
                      <label htmlFor="certificateName" className="form-label">
                        Nama pada Sertifikat
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="certificateName"
                        value={certificateName}
                        onChange={(e) => setCertificateName(e.target.value)}
                        placeholder="Masukkan nama lengkap"
                      />
                    </div>
                    <div className="alert alert-warning">
                      <i className="isax isax-warning-2 me-2" />
                      Pastikan nama sudah benar, karena e-sertifikat hanya dapat
                      di generate satu kali.
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowCertificateModal(false)}
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={confirmCertificateDownload}
                      disabled={!certificateName.trim()}
                    >
                      Download Sertifikat
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  const currentQ = questions[currentQuestion]

  // Loading state
  if (loading) {
    return (
      <div className="content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="card text-center">
                <div className="card-body">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Memuat data CBT...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="card text-center">
                <div className="card-body">
                  <div className="text-danger mb-3">
                    <i
                      className="isax isax-warning-2"
                      style={{ fontSize: '3rem' }}
                    />
                  </div>
                  <h5 className="text-danger">Error</h5>
                  <p className="mb-4">{error}</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => window.location.reload()}
                  >
                    <i className="isax isax-refresh me-2" />
                    Coba Lagi
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // No questions available
  if (!questions.length) {
    return (
      <div className="content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="card text-center">
                <div className="card-body">
                  <div className="text-warning mb-3">
                    <i
                      className="isax isax-document-text"
                      style={{ fontSize: '3rem' }}
                    />
                  </div>
                  <h5>Tidak ada soal tersedia</h5>
                  <p className="mb-4">
                    CBT ini belum memiliki soal yang dapat dikerjakan.
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={handleGoToDashboard}
                  >
                    <i className="isax isax-home me-2" />
                    Kembali ke Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="row">
          {/* Timer and Progress */}
          <div className="col-12 mb-4">
            <div className="card">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <h5 className="mb-0">
                      CBT Test - {cbtData?.title || 'Loading...'}
                    </h5>
                    <small className="text-muted">
                      Soal {currentQuestion + 1} dari {questions.length}
                    </small>
                  </div>
                  <div className="col-md-6 text-end">
                    <div className="d-flex justify-content-end align-items-center gap-3">
                      <div className="text-center">
                        <div className="h4 mb-0 text-warning">
                          {formatTime(timeLeft)}
                        </div>
                        <small className="text-muted">Waktu Tersisa</small>
                      </div>
                      <div
                        className="progress flex-grow-1"
                        style={{ maxWidth: '200px' }}
                      >
                        <div
                          className="progress-bar bg-success"
                          style={{
                            width: `${((currentQuestion + 1) / questions.length) * 100
                              }%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Question */}
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body">

                {currentQ?.image && (
                  <div className="d-flex align-items-center justify-content-center">
                    <img
                      className="img-fluid object-fit-cover mb-3"
                      style={{ maxHeight: '400px' }}
                      src={
                        currentQ.image.startsWith('http')
                          ? currentQ.image
                          : `${process.env.REACT_APP_BASE_URL_API || 'http://localhost:8000'}/storage/${currentQ.image.replace(/^\/?(public\/|storage\/)*/, '')}`
                      }
                      alt={currentQ?.image}
                    />
                  </div>
                )}

                <h5 className="mb-4 fw-normal" dangerouslySetInnerHTML={{ __html: currentQ.question }}></h5>

                <div className="options-list">
                  {currentQ.answers.map((answer, index) => (
                    <div
                      key={index}
                      className={`option-item p-3 border rounded mb-3 cursor-pointer ${answers[currentQuestion] === index
                        ? 'border-primary bg-light'
                        : ''
                        }`}
                      onClick={() => handleAnswerSelect(index)}
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className={`option-circle me-3 ${answers[currentQuestion] === index
                            ? 'bg-primary text-white'
                            : 'border'
                            }`}
                        >
                          {String.fromCharCode(65 + index)}
                        </div>
                        <span>{answer.choice}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between mt-4">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handlePreviousQuestion}
                    disabled={currentQuestion === 0}
                  >
                    <i className="isax isax-arrow-left me-2" />
                    Sebelumnya
                  </button>

                  <div className="d-flex gap-2">
                    <button
                      className={`btn ${doubtful[currentQuestion]
                        ? 'btn-warning'
                        : 'btn-outline-warning'
                        }`}
                      onClick={() => {
                        const updated = [...doubtful]
                        updated[currentQuestion] = !updated[currentQuestion]
                        setDoubtful(updated)
                      }}
                    >
                      Ragu
                    </button>

                    {currentQuestion === questions.length - 1 ? (
                      <button
                        className="btn btn-success"
                        onClick={() => setShowSubmitConfirm(true)}
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Menyimpan...
                          </>
                        ) : (
                          <>
                            <i className="isax isax-tick-circle me-2" />
                            Selesai & Submit
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary"
                        onClick={handleNextQuestion}
                      >
                        Selanjutnya
                        <i className="isax isax-arrow-right ms-2" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Question Navigator */}
          <div className="col-lg-4">
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">Navigasi Soal</h6>
              </div>
              <div className="card-body">
                <div className="row g-2">
                  {questions.map((_, index) => (
                    <div key={index} className="col-3">
                      <button
                        className={`btn btn-sm w-100 ${index === currentQuestion
                          ? 'btn-primary'
                          : doubtful[index]
                            ? 'btn-warning'
                            : answers[index] !== undefined &&
                              answers[index] !== -1
                              ? 'btn-success'
                              : 'btn-outline-secondary'
                          }`}
                        onClick={() => handleQuestionJump(index)}
                      >
                        {index + 1}
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-3">
                  <small className="text-muted">
                    <span className="badge bg-success me-2">●</span> Sudah
                    dijawab
                    <br />
                    <span className="badge bg-warning me-2">●</span> Ragu-ragu
                    <br />
                    <span className="badge bg-primary me-2">●</span> Soal saat
                    ini
                    <br />
                    <span className="badge bg-secondary me-2">●</span> Belum
                    dijawab
                  </small>
                </div>
              </div>
            </div>

            {currentQ?.normal_lab ? (
              <div className="card mt-3">
                <div className="card-header">
                  <h6 className="mb-0">Normal Lab</h6>
                </div>
                <div className="card-body">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() =>
                      window.open(
                        currentQ.normal_lab,
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                  >
                    <i className="isax isax-document-text me-2" />
                    Buka Normal Lab
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <style>{`
         .cursor-pointer {
           cursor: pointer;
         }
         .option-circle {
           width: 30px;
           height: 30px;
           border-radius: 50%;
           display: flex;
           align-items: center;
           justify-content: center;
           font-weight: bold;
         }
       `}</style>

      {/* Certificate Download Modal */}
      {showCertificateModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Download Sertifikat</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCertificateModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Apakah Anda yakin ingin mencetak e-sertifikat dengan nama
                    yang tertera pada formulir di bawah ini?
                  </p>
                  <div className="mb-3">
                    <label htmlFor="certificateName" className="form-label">
                      Nama pada Sertifikat
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="certificateName"
                      value={certificateName}
                      onChange={(e) => setCertificateName(e.target.value)}
                      placeholder="Masukkan nama lengkap"
                    />
                  </div>
                  <div className="alert alert-warning">
                    <i className="isax isax-warning-2 me-2" />
                    Pastikan nama sudah benar, karena e-sertifikat hanya dapat
                    di generate satu kali.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCertificateModal(false)}
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmCertificateDownload}
                    disabled={!certificateName.trim()}
                  >
                    Download Sertifikat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Konfirmasi Selesai Ujian</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowSubmitConfirm(false)}
                  />
                </div>
                <div className="modal-body">
                  <p>
                    Apakah kamu yakin akan mengirim jawaban ujian CBT? Kamu masih
                    bisa mengulangi CBT selama masih ada kesempatan.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowSubmitConfirm(false)}
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setShowSubmitConfirm(false)
                      handleFinishTest()
                    }}
                    disabled={submitting}
                  >
                    Ya, Kirim Jawaban
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default CBTTest
